import type { ReactNode } from "react";
import { Spinner } from "@soma/ui";
import { cn } from "@/lib/utils";

export type TagTone = "ok" | "accent" | "muted" | "solid";
export type TagSize = "sm" | "xs";

const TONES: Record<TagTone, string> = {
  ok: "border-transparent bg-ok-soft text-ok",
  accent: "border-transparent bg-accent-soft text-accent",
  muted: "border-line text-fg-muted",
  solid: "border-transparent bg-btn text-btn-fg",
};

// xs is for a tag repeated down a table column, where the label is read as a category
// rather than an announcement and every pixel of height is a pixel of row.
const SIZES: Record<TagSize, string> = {
  sm: "h-6 px-2.5 text-[11px]",
  xs: "h-[18px] px-1.5 text-[9.5px]",
};

/**
 * A small state label: validator health, a miner's status, the mode a competition is in.
 * `dot` adds the marker that makes a live state read as live rather than as a category.
 */
export function Tag({
  tone,
  size = "sm",
  dot = false,
  busy = false,
  children,
}: {
  tone: TagTone;
  size?: TagSize;
  dot?: boolean;
  /** Renders a spinner in place of the dot: this state is a stage, not a conclusion. */
  busy?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-mono font-medium tracking-[0.08em] uppercase",
        SIZES[size],
        TONES[tone],
      )}
    >
      {busy ? (
        <Spinner className="size-2.5" />
      ) : (
        dot && <span aria-hidden className={cn("size-1.5 rounded-full bg-current", tone === "ok" && "dot-live")} />
      )}
      {children}
    </span>
  );
}
